import { fetchAuthSession } from "aws-amplify/auth";
import amplifyOutputs from "@/amplify_outputs.json";
import { deleteQuickSightUser } from './quicksight.js';

const USER_MANAGEMENT_CONFIG = {
  lambdaUrl: amplifyOutputs.custom?.userManagementFunctionUrl || "",
};

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (action, data = {}) => {
  try {
    const session = await fetchAuthSession();
    const jwtToken = session.tokens?.accessToken?.toString();

    if (!jwtToken) {
      throw new Error("No authentication token found");
    }

    const response = await fetch(USER_MANAGEMENT_CONFIG.lambdaUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        action,
        ...data,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error in ${action}:`, error);
    throw error;
  }
};

// CORE USER MANAGEMENT FUNCTIONS

// Register or invite a user in Cognito
export const createUser = async ({ userEmail, password }) => {
  // Only include password if provided
  const data = password ? { userEmail, password } : { userEmail };
  return makeAuthenticatedRequest("CREATE_USER", data);
};


// Delete a user (from both Cognito and QuickSight)
export const deleteUser = async (userEmail) => {
  if (!userEmail) {
    throw new Error("User email is required");
  }

  const results = {
    cognito: { success: false, error: null },
    quicksight: { success: false, error: null }
  };

  try {
    // First, try to delete from QuickSight (if it fails, we'll still continue with Cognito)
    try {
      await deleteQuickSightUser(userEmail);
      results.quicksight.success = true;
    } catch (quicksightError) {
      console.warn(`QuickSight deletion failed for ${userEmail}:`, quicksightError);
      results.quicksight.error = quicksightError.message;
      // Continue with Cognito deletion even if QuickSight fails
    }

    // Delete from Cognito
    const cognitoResult = await makeAuthenticatedRequest("DELETE_USER", { userEmail });
    results.cognito.success = true;

    // Return success if Cognito deletion succeeded
    return {
      success: true,
      message: `User ${userEmail} deleted successfully`,
      details: results,
      result: cognitoResult
    };

  } catch (error) {
    results.cognito.error = error.message;
    
    // If Cognito deletion fails, throw the error
    throw new Error(`Failed to delete user ${userEmail}: ${error.message}`);
  }
};

// Update user information
export const updateUser = async (userEmail, updateData) => {
  if (!userEmail) {
    throw new Error("User email is required");
  }

  // Validate that at least one field is being updated
  if (!updateData || (Object.keys(updateData).length === 0)) {
    throw new Error("At least one field must be updated");
  }

  const allowedFields = ['newEmail', 'newPassword'];
  const filteredData = Object.keys(updateData)
    .filter(key => allowedFields.includes(key))
    .reduce((obj, key) => {
      obj[key] = updateData[key];
      return obj;
    }, {});

  // Check if any valid fields remain after filtering
  if (Object.keys(filteredData).length === 0) {
    throw new Error("No valid fields to update");
  }

  // Validate new email if provided
  if (filteredData.newEmail) {
    if (!validateEmail(filteredData.newEmail)) {
      throw new Error("Invalid new email format");
    }
    
    // Check if the new email is the same as the current email
    if (filteredData.newEmail === userEmail) {
      throw new Error("New email must be different from current email");
    }
  }

  // Validate new password if provided
  if (filteredData.newPassword) {
    const passwordValidation = validatePassword(filteredData.newPassword);
    if (!passwordValidation.isValid) {
      throw new Error(passwordValidation.message);
    }
  }

  try {
    return await makeAuthenticatedRequest("UPDATE_USER", {
      userEmail,
      ...filteredData,
    });
  } catch (error) {
    // Enhanced error handling for update-specific errors
    if (error.message.includes("Email already exists")) {
      throw new Error("This email address is already in use by another user");
    }
    if (error.message.includes("User not found")) {
      throw new Error("User not found. Please refresh and try again");
    }
    if (error.message.includes("Invalid password")) {
      throw new Error("Password does not meet the security requirements");
    }
    
    // Re-throw other errors
    throw error;
  }
};

// Get all users
export const getUsers = async () => {
  try {
    const response = await makeAuthenticatedRequest("GET_USERS");
    return response.users || [];
  } catch (error) {
    console.error("Error fetching users:", error);
    throw error;
  }
};

// Get a specific user
export const getUser = async (userEmail) => {
  if (!userEmail) {
    throw new Error("User email is required");
  }

  try {
    const response = await makeAuthenticatedRequest("GET_USER", { userEmail });
    return response.user;
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
};

// Enable a user
export const enableUser = async (userEmail) => {
  if (!userEmail) {
    throw new Error("User email is required");
  }

  return makeAuthenticatedRequest("ENABLE_USER", { userEmail });
};

// Disable a user
export const disableUser = async (userEmail) => {
  if (!userEmail) {
    throw new Error("User email is required");
  }

  return makeAuthenticatedRequest("DISABLE_USER", { userEmail });
};

// VALIDATION FUNCTIONS

// Helper function to validate email format
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};



// Helper function to validate password strength
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, message: "Password is required" };
  }

  if (password.length < 8) {
    return { isValid: false, message: "Password must be at least 8 characters long" };
  }

  if (!/(?=.*[a-z])/.test(password)) {
    return { isValid: false, message: "Password must contain at least one lowercase letter" };
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    return { isValid: false, message: "Password must contain at least one uppercase letter" };
  }

  if (!/(?=.*\d)/.test(password)) {
    return { isValid: false, message: "Password must contain at least one number" };
  }

  if (!/(?=.*[!@#$%^&*])/.test(password)) {
    return { isValid: false, message: "Password must contain at least one special character (!@#$%^&*)" };
  }

  return { isValid: true, message: "Password is valid" };
};

// UTILITY FUNCTIONS

// Debug function to list all users (for testing)
export const debugListAll = async () => {
  try {
    console.log("Debug: Fetching all users");
    const users = await getUsers();
    console.log("Debug: Found users:", users.length);
    return users;
  } catch (error) {
    console.error("Debug: Error listing users:", error);
    throw error;
  }
};

// Helper function to format user data for display
export const formatUserData = (users) => {
  return users.map(user => ({
    id: user.username,
    username: user.username,
    email: user.email,
    status: user.status,
    enabled: user.enabled,
    created: user.created,
    lastModified: user.lastModified,
  }));
};

// Helper function to get user by email
export const getUserByEmail = async (userEmail) => {
  const users = await getUsers();
  return users.find(user => user.email === userEmail);
};

// Helper function to check if user exists
export const userExists = async (userEmail) => {
  try {
    const user = await getUserByEmail(userEmail);
    return !!user;
  } catch (error) {
    console.error("Error checking if user exists:", error);
    return false;
  }
};

// Get user statistics
export const getUserStatistics = async () => {
  try {
    const users = await getUsers();
    const stats = {
      totalUsers: users.length,
      activeUsers: users.filter(u => u.status === 'CONFIRMED').length,
      inactiveUsers: users.filter(u => u.status !== 'CONFIRMED').length,
      enabledUsers: users.filter(u => u.enabled).length,
      disabledUsers: users.filter(u => !u.enabled).length,
    };
    return stats;
  } catch (error) {
    console.error("Error getting user statistics:", error);
    throw error;
  }
};

// Search users by various criteria
export const searchUsers = async (searchTerm) => {
  try {
    const users = await getUsers();
    const searchLower = searchTerm.toLowerCase();
    
    return users.filter(user => 
      user.username?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  } catch (error) {
    console.error("Error searching users:", error);
    throw error;
  }
};

// ERROR HANDLING

// Error handling helper
export const handleUserManagementError = (error) => {
  if (error.message.includes("User already exists")) {
    return "A user with this email already exists";
  }
  if (error.message.includes("Invalid email")) {
    return "Please enter a valid email address";
  }
  if (error.message.includes("Password")) {
    return error.message; // Password validation messages are already user-friendly
  }
  if (error.message.includes("No authentication token")) {
    return "Please log in again to continue";
  }
  if (error.message.includes("403")) {
    return "You don't have permission to perform this action";
  }
  if (error.message.includes("404")) {
    return "User not found";
  }
  if (error.message.includes("500")) {
    return "Server error. Please try again later";
  }
  
  return error.message || "An unexpected error occurred";
};

// BATCH OPERATIONS

// Batch delete users
export const batchDeleteUsers = async (userEmails) => {
  const results = [];
  
  for (const email of userEmails) {
    try {
      const result = await deleteUser(email);
      results.push({ email, success: true, result });
    } catch (error) {
      results.push({ email, success: false, error: error.message });
    }
  }
  
  return results;
};

// Batch enable users
export const batchEnableUsers = async (userEmails) => {
  const results = [];
  
  for (const email of userEmails) {
    try {
      const result = await enableUser(email);
      results.push({ email, success: true, result });
    } catch (error) {
      results.push({ email, success: false, error: error.message });
    }
  }
  
  return results;
};

// Batch disable users
export const batchDisableUsers = async (userEmails) => {
  const results = [];
  
  for (const email of userEmails) {
    try {
      const result = await disableUser(email);
      results.push({ email, success: true, result });
    } catch (error) {
      results.push({ email, success: false, error: error.message });
    }
  }
  
  return results;
};

// CONFIGURATION AND DEBUG

// Configuration helper
export const getUserManagementConfig = () => {
  return {
    lambdaUrl: USER_MANAGEMENT_CONFIG.lambdaUrl,
    isConfigured: !!USER_MANAGEMENT_CONFIG.lambdaUrl,
  };
};

// Debug helper for development
export const debugUserManagement = async () => {
  const config = getUserManagementConfig();
  console.log("User Management Config:", config);
  
  try {
    const session = await fetchAuthSession();
    console.log("Auth Session:", {
      hasTokens: !!session.tokens,
      hasAccessToken: !!session.tokens?.accessToken,
    });
  } catch (error) {
    console.error("Auth Session Error:", error);
  }
  
  return config;
};

// Test connection to Lambda
export const testConnection = async () => {
  try {
    const config = getUserManagementConfig();
    if (!config.isConfigured) {
      throw new Error("User management Lambda URL not configured");
    }
    
    // Try to get users as a connection test
    await getUsers();
    return { success: true, message: "Connection successful" };
  } catch (error) {
    return { success: false, error: error.message };
  }
};